import { useMutation, useQuery } from "@apollo/client/react";
import * as Dialog from "@radix-ui/react-dialog";
import Image from "next/image";
import { useCallback, useState } from "react";

import { graphql, useFragment } from "../../../../__generated__/gql";
import type {
  AccommodationFeeInput,
  Scalars,
} from "../../../../__generated__/gql/graphql";
import { LEGAL_DOCUMENTS } from "../../../../constants";
import { useGtm } from "../../../../lib/google-tag-manager/gtm";
import { sendReservationUpdatedEvent } from "../../../../lib/google-tag-manager/reservation-form";
import { useAlertDialog } from "../../../_contexts/alert-dialog";
import { useSnackbar } from "../../../_contexts/snackbar";
import { useAsyncAction } from "../../../_hooks/use-async-action";
import { useInNativeApp } from "../../../_hooks/use-in-native-app";
import { postMessageToNativeApp } from "../../../utils/native-app";
import { DialogOverlay } from "../../dialog-overlay";
import { ChangeOrCancelFee } from "../../reservation/change-or-cancel-fee";
import {
  ReservationSummary,
  ReservationSummaryCard,
  ReservationSummaryFragment,
  type ReservationSummaryData,
} from "../../reservation/reservation-summary";
import { Link } from "../../ui/link";
import { CancelFeePolicyArea } from "../cancel-fee-policy-area";
import { reservationErrorMessageOnChange } from "../error-messages";

const ReservationQuery = graphql(`
  query AccommodationReservationForChange($id: ID!) {
    accommodationReservation(id: $id) {
      id
      accommodation {
        id
        name
      }
      provider {
        __typename
      }
      firstNight
      lastNight
      reservedFrom
      reservedTo
      detail {
        numberOfAdults
        numberOfKids
        numberOfInfants
        numberOfTotalChildren
        previousLocation
        nextLocation
        bringDog
        numberOfDogs
        useEvCharger
        allowRoomChange
        internalStamp
      }
      stayTicketUnit {
        stayTicketMasterId
        ...StayTicketUnitForCancelFeePolicyFragment
      }
      ...ReservationSummary
      ...AccommodationReservationInfo
    }
  }
`);

const ChangeFeeQuery = graphql(`
  query AccommodationReservationChangeFee(
    $fromAccommodationReservationId: ID!
    $to: EstimatedChangeFeeOfAccommodationReservationInput!
    $internal: Boolean
  ) {
    estimatedChangeFeeOfAccommodationReservation(
      fromAccommodationReservationId: $fromAccommodationReservationId
      to: $to
      internal: $internal
    ) {
      ... on EstimatedChargeOfAccommodationReservation {
        items {
          __typename
        }
      }
      ...ChangeOrCancelEstimatedChargeFragment
    }
  }
`);

const Mutation = graphql(`
  mutation ChangeAccommodationReservation(
    $id: ID!
    $input: UpdateAccommodationReservationInput!
  ) {
    changeAccommodationReservation(id: $id, input: $input) {
      ... on AccommodationReservation {
        id
      }
      ... on ChangeAccommodationReservationError {
        case
        internalCode
      }
    }
  }
`);

const InternalMutation = graphql(`
  mutation InternalChangeAccommodationReservation(
    $id: ID!
    $input: InternalUpdateAccommodationReservationInput!
  ) {
    internalChangeAccommodationReservation(id: $id, input: $input) {
      ... on AccommodationReservation {
        id
      }
      ... on ChangeAccommodationReservationError {
        case
        internalCode
      }
    }
  }
`);

// draftReservationはDraftAccommodationReservationから来るため、
// ReservationSummaryDataを拡張してmutation用のフィールドを追加
type Reservation = ReservationSummaryData & {
  accommodation: ReservationSummaryData["accommodation"] & {
    id: string;
  };
  firstNight: Scalars["PlainDate"]["output"];
  lastNight: Scalars["PlainDate"]["output"];
  detail: ReservationSummaryData["detail"] & {
    bringDog: boolean;
    numberOfDogs: number;
    useEvCharger: boolean;
  };
};

type InternalFormOptions = {
  waiveConsumedSuppliesFee?: boolean;
};

type DialogProps = {
  reservationId: string;
  draftReservation: Reservation;
  // Admin 予約時に料金を変更する場合に使用
  internalFee?: AccommodationFeeInput;
  // Admin 予約時のフォームオプション
  internalFormOptions?: InternalFormOptions;
  // Admin 予約変更時、変更前の DP 料金を維持する場合に true
  preserveDynamicPricing?: boolean;
};

export function useChangeReservationDialog() {
  const [state, setState] = useState<DialogProps | null>(null);

  const openDialog = useCallback((state: DialogProps) => {
    setState(state);
  }, []);

  const renderDialog = useCallback(
    ({
      isInternal,
      onComplete,
    }: {
      isInternal?: boolean;
      onComplete: () => void;
    }) => {
      if (state == null) return null;
      return (
        <ChangeReservationDialog
          {...state}
          setClose={() => setState(null)}
          isInternal={isInternal}
          onComplete={onComplete}
        />
      );
    },
    [state],
  );

  return { renderDialog, openDialog };
}

function ChangeReservationDialog({
  reservationId,
  draftReservation,
  internalFee,
  internalFormOptions,
  preserveDynamicPricing,
  isInternal = false,
  setClose,
  onComplete,
}: DialogProps & {
  isInternal?: boolean;
  setClose: () => void;
  onComplete: () => void;
}) {
  const { data: reservationData } = useQuery(ReservationQuery, {
    variables: {
      id: reservationId,
    },
  });

  const original = reservationData?.accommodationReservation;
  const draft = draftReservation;

  const { data: estimationData } = useQuery(ChangeFeeQuery, {
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      fromAccommodationReservationId: original?.id!,
      to: {
        firstNight: draft.firstNight,
        lastNight: draft.lastNight,
        accommodationId: draft.accommodation.id,
        options: {
          bringDog: draft.detail.bringDog,
          numberOfDogs: draft.detail.bringDog ? draft.detail.numberOfDogs : 0,
          useEvCharger: draft.detail.useEvCharger,
        },
        // 変更後の人数情報を反映する。AdditionalGuestFeePolicy 紐付き master の場合、
        // 追加ゲスト料金が変更見積もりに反映される。
        // 変更見積もりに追加ゲスト料金の差分を反映するロジック自体は BZA-35 で対応。
        guestCounts: {
          numberOfAdults: draft.detail.numberOfAdults,
          numberOfKids: draft.detail.numberOfKids,
          numberOfInfants: draft.detail.numberOfInfants,
        },
      },
      internal: isInternal,
    },
    skip: !reservationData,
  });

  const [changeReservation, { loading: changeReservationLoading }] =
    useMutation(Mutation);
  const [
    internalChangeReservation,
    { loading: internalChangeReservationLoading },
  ] = useMutation(InternalMutation);

  const showSnackbar = useSnackbar();
  const showAlertDialog = useAlertDialog();
  const gtm = useGtm();
  const { isPending, execute } = useAsyncAction();
  const isInNativeApp = useInNativeApp();

  // originalをReservationSummaryCard用に展開（フラグメントなしのdraftと比較するため）
  const originalForCard = useFragment(ReservationSummaryFragment, original);

  if (!estimationData || !original || !draft) return <DialogOverlay />;

  const estimation =
    estimationData.estimatedChangeFeeOfAccommodationReservation;

  if (estimation.__typename !== "EstimatedChargeOfAccommodationReservation") {
    alert("エラーが発生しました");
    return null;
  }

  const handleChange = async () => {
    execute(async () => {
      const options = {
        variables: {
          id: reservationId,
          input: {
            accommodationId: draft.accommodation.id,
            firstNight: draft.firstNight,
            lastNight: draft.lastNight,
            detail: {
              numberOfAdults: draft.detail.numberOfAdults,
              numberOfKids: draft.detail.numberOfKids,
              numberOfInfants: draft.detail.numberOfInfants,
              previousLocation: draft.detail.previousLocation ?? "",
              nextLocation: draft.detail.nextLocation ?? "",
              useEvCharger: draft.detail.useEvCharger,
              bringDog: draft.detail.bringDog,
              numberOfDogs: draft.detail.bringDog
                ? draft.detail.numberOfDogs
                : 0,
              allowRoomChange: draft.detail.allowRoomChange,
              internalStamp: draft.detail.internalStamp ?? null,
            },
            fee: isInternal ? internalFee : undefined,
            preserveDynamicPricing: isInternal
              ? (preserveDynamicPricing ?? true)
              : undefined,
          },
        },
      };
      let result;
      if (isInternal) {
        const { data } = await internalChangeReservation(options);
        result = data?.internalChangeAccommodationReservation;
      } else {
        const { data } = await changeReservation(options);
        result = data?.changeAccommodationReservation;
      }

      switch (result?.__typename) {
        case "AccommodationReservation": {
          setClose();
          onComplete();
          // Internalの場合はsnackbarを表示しない（呼び出し元で成功ダイアログを表示するため）
          // アプリの場合もsnackbarを表示しない
          if (!isInternal && !isInNativeApp) {
            setTimeout(() => {
              showSnackbar({
                message: "予約変更が完了しました。",
              });
            }, 1500);
          }
          if (isInNativeApp) {
            // アプリに通知する
            const messageType =
              original?.provider.__typename ===
              "OneMoreCabinAccommodationReservationProvider"
                ? "OneMoreCabinChange"
                : "ReservationChange";
            postMessageToNativeApp(
              {
                type: messageType,
                payload: {
                  reservationId: result.id,
                },
              },
              window,
            );
          }
          sendReservationUpdatedEvent({
            provider: original.provider,
            ticketMasterId: original.stayTicketUnit?.stayTicketMasterId,
            platform: isInNativeApp ? "app" : "web",
          });
          break;
        }
        case "ChangeAccommodationReservationError": {
          setClose();
          const [mediumBody, normalBody] = reservationErrorMessageOnChange(
            result.case,
          );
          showAlertDialog({ title: "予約エラー", mediumBody, normalBody });
          if (isInNativeApp) {
            const messageType =
              original?.provider.__typename ===
              "OneMoreCabinAccommodationReservationProvider"
                ? "OneMoreCabinChange"
                : "ReservationChange";
            postMessageToNativeApp(
              {
                type: messageType,
                payload: {
                  case: "GeneralError",
                },
              },
              window,
            );
          }
          break;
        }
      }
    });
  };

  return (
    <Dialog.Root
      defaultOpen
      onOpenChange={(open) => {
        if (!open) setClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="sd-overlay">
          <Dialog.Content
            className="sd-content flex w-[315px] flex-col p-[30px] md:w-[690px]"
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <Dialog.Title className="text-skogecha mb-[30px] text-[14px] leading-none font-bold">
              予約変更の確認
            </Dialog.Title>
            <div className="text-[13px] leading-[1.8] font-medium">
              下記のように予約を変更します。
              <br />
              ご確認の上、「予約を変更する」ボタンを押してください。
            </div>
            <div className="mt-[30px] w-fit">
              {original.stayTicketUnit ||
              original.provider.__typename ===
                "OnlineTravelAgentAccommodationReservationProvider" ? (
                <div className="text-[12px]">
                  <CancelFeePolicyArea
                    stayTicketUnit={original.stayTicketUnit}
                    noShowNoticeHidden={true}
                    cannotChangeReservationNoticeHidden={true}
                  />
                </div>
              ) : (
                <div className="border-b-sorange border-b pb-[8px] text-[12px] leading-[1.1] font-bold">
                  <Link
                    data-analytics-id="reservation_form_clicked_cancel_fee_policy"
                    href={LEGAL_DOCUMENTS.cancelFeePolicy.url}
                    target="_blank"
                  >
                    キャンセル料金のルールについて
                  </Link>
                </div>
              )}
            </div>
            <div className="mt-[30px] flex flex-col justify-between md:flex-row">
              <ReservationSummary
                reservationData={original}
                isInternal={isInternal}
              />
              <div className="my-[10px] flex items-center justify-center md:-rotate-90">
                <Image
                  src="/i/icon_arrow_down.svg"
                  alt="down"
                  width={30}
                  height={30}
                />
              </div>
              <ReservationSummaryCard
                reservation={draftReservation}
                comparison={originalForCard}
                isInternal={isInternal}
                formOptions={internalFormOptions}
              />
            </div>
            <div className="mt-[40px]"></div>
            {estimation.items.length > 0 && (
              <ChangeOrCancelFee estimationData={estimation} />
            )}
            <div className="mt-[4px] mb-[23px] text-[12px] leading-none font-medium">
              {estimation.items.length > 0 ? (
                <>変更料金の決済後に、予約が変更されます。</>
              ) : (
                <>変更料金はかかりません。</>
              )}
            </div>
            <div className="mt-[40px] flex flex-col gap-[10px] text-[12px] md:flex-row md:gap-[20px]">
              <button
                data-analytics-id="reservation_form_submitted_change"
                onClick={handleChange}
                className="btn-primary md:w-[420px]"
                disabled={
                  isPending ||
                  changeReservationLoading ||
                  internalChangeReservationLoading
                }
              >
                予約を変更する
              </button>
              <button
                data-analytics-id="reservation_form_dismissed_change"
                onClick={setClose}
                className="btn-secondary md:w-[190px]"
                disabled={isPending}
              >
                変更しない
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
